import { useEffect, useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangleIcon, PackagePlusIcon, XIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { AnimalRow } from '@/services/animal-management';
import {
  type AddAnimalsToShipmentPayload,
  type DummyShipment,
  getShipmentConflicts,
  getShipments,
  getShipmentStatusLabel,
  type ShipmentConflict,
  shipmentStatusChangeOptions,
} from '@/services/animal-management';
import { useCurrentUser } from '@/services/auth/util';

type AddToShipmentDialogProps = {
  open: boolean;
  selectedAnimals: AnimalRow[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: AddAnimalsToShipmentPayload) => void;
};

const IN_PROGRESS = 100000002;
const SHIPPED = 100000000;

const createAddToShipmentSchema = ({
  requireNextStatus,
  requireOverride,
}: {
  requireNextStatus: boolean;
  requireOverride: boolean;
}) =>
  z
    .object({
      shipmentId: z.string().trim().min(1),
      overrideConflicts: z.boolean().default(false),
      nextStatus: z
        .union([z.literal('100000001'), z.literal('100000007')])
        .optional(),
    })
    .superRefine((values, ctx) => {
      if (requireOverride && !values.overrideConflicts) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'override-required',
          path: ['overrideConflicts'],
        });
      }

      if (requireNextStatus && !values.nextStatus) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'status-required',
          path: ['nextStatus'],
        });
      }
    });

type AddToShipmentFormValues = z.infer<
  ReturnType<typeof createAddToShipmentSchema>
>;

const statusToneClasses: Record<number, string> = {
  100000000: 'border-slate-300 bg-slate-100 text-slate-700',
  100000001: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  100000002: 'border-amber-300 bg-amber-50 text-amber-800',
  100000007: 'border-blue-300 bg-blue-50 text-blue-700',
};

function ShipmentStatusTag({ status }: { status: DummyShipment['status'] }) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full border px-2.5 py-1 text-xs font-medium',
        statusToneClasses[status],
      )}
    >
      {getShipmentStatusLabel(status)}
    </span>
  );
}

function ConflictSummary({ conflicts }: { conflicts: ShipmentConflict[] }) {
  const { t } = useTranslation();

  return (
    <Card className="border-amber-300 bg-amber-50/90">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <AlertTriangleIcon className="size-4" />
          {t('animalManagement.addToShipment.conflictTitle', {
            defaultValue: 'Active shipment conflict for {count} animals',
            count: conflicts.length,
          })}
        </CardTitle>
        <CardDescription className="text-amber-800">
          {t(
            'animalManagement.addToShipment.conflictDescription',
            'These animals are already assigned to an In Progress shipment. Confirm the move before continuing.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-sm text-amber-950">
        {conflicts.map(({ animal, shipment }) => (
          <div
            key={`${shipment.id}-${animal.id}`}
            className="rounded-md border border-amber-200 bg-white/70 px-3 py-2"
          >
            <div className="font-medium">{animal.nameTag}</div>
            <div>
              {t('animalManagement.addToShipment.currentShipment', {
                defaultValue: 'Currently on {shipmentNumber} for {customer}',
                shipmentNumber: shipment.shipmentNumber,
                customer: shipment.customer,
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function AddToShipmentDialog({
  open,
  selectedAnimals,
  onOpenChange,
  onSubmit: handleSubmit,
}: AddToShipmentDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const overrideCheckboxId = useId();
  const { data: currentUser } = useCurrentUser();
  const [shipmentSearch, setShipmentSearch] = useState('');

  const shipments = useMemo(
    () => getShipments(shipmentSearch),
    [shipmentSearch],
  );

  const [selectedShipmentId, setSelectedShipmentId] = useState('');

  const selectedShipment = useMemo(
    () =>
      shipments.find((shipment) => shipment.id === selectedShipmentId) ?? null,
    [selectedShipmentId, shipments],
  );

  const conflicts = useMemo(
    () => getShipmentConflicts(selectedAnimals, selectedShipment?.id),
    [selectedAnimals, selectedShipment?.id],
  );

  const requiresStatusChange =
    selectedShipment?.status === IN_PROGRESS ||
    selectedShipment?.status === SHIPPED;

  const schema = useMemo(
    () =>
      createAddToShipmentSchema({
        requireNextStatus: requiresStatusChange,
        requireOverride: conflicts.length > 0,
      }),
    [conflicts.length, requiresStatusChange],
  );

  const form = useForm<AddToShipmentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      shipmentId: '',
      overrideConflicts: false,
      nextStatus: undefined,
    },
  });

  const shipmentStatusItems = useMemo(
    () =>
      shipmentStatusChangeOptions.map((option) => ({
        label: option.label,
        value: option.value.toString(),
      })),
    [],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setShipmentSearch('');
    setSelectedShipmentId('');
    form.reset({
      shipmentId: '',
      overrideConflicts: false,
      nextStatus: undefined,
    });
  }, [form, open, selectedAnimals]);

  useEffect(() => {
    form.setValue('shipmentId', selectedShipmentId, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (!requiresStatusChange) {
      form.setValue('nextStatus', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, requiresStatusChange, selectedShipmentId]);

  const submit = (values: AddToShipmentFormValues) => {
    handleSubmit({
      shipmentId: values.shipmentId,
      animals: selectedAnimals,
      overrideConflicts: values.overrideConflicts,
      nextStatus: values.nextStatus
        ? (Number(values.nextStatus) as 100000001 | 100000007)
        : undefined,
      userIdentity: currentUser?.displayName ?? currentUser?.email,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              'animalManagement.addToShipment.title',
              'Add to Existing Shipment',
            )}
          </DialogTitle>
          <DialogDescription>
            {t('animalManagement.addToShipment.description', {
              defaultValue:
                'Review {count} selected animals and choose a shipment. Scheduled and Incomplete shipments are ready to add immediately.',
              count: selectedAnimals.length,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody
          as="form"
          id={formId}
          onSubmit={form.handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <Card className="border-blue-200 bg-blue-50/70">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                {t('animalManagement.addToShipment.selectedAnimals', {
                  defaultValue: 'Selected Animals ({count})',
                  count: selectedAnimals.length,
                })}
              </CardTitle>
              <CardDescription>
                {t(
                  'animalManagement.addToShipment.selectedAnimalsHelper',
                  'Confirm the animals you want to add before choosing a shipment.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {selectedAnimals.map((animal) => (
                <div
                  key={animal.id}
                  className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-3 py-1.5 text-sm text-blue-950"
                >
                  <span className="font-medium">{animal.nameTag}</span>
                  <span className="text-xs text-blue-700">
                    {animal.animalType}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-900">
                {t(
                  'animalManagement.addToShipment.searchLabel',
                  'Find Shipment',
                )}
              </CardTitle>
              <CardDescription>
                {t(
                  'animalManagement.addToShipment.searchHelper',
                  'Search by shipment number, customer, route, or status.',
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Input
                value={shipmentSearch}
                onChange={(event) => setShipmentSearch(event.target.value)}
                placeholder={t(
                  'animalManagement.addToShipment.searchPlaceholder',
                  'Search shipments...',
                )}
              />
            </CardContent>
          </Card>

          <Field data-invalid={form.formState.errors.shipmentId != null}>
            <FieldLabel>
              {t('animalManagement.addToShipment.resultsLabel', {
                defaultValue: 'Shipment Results ({count})',
                count: shipments.length,
              })}
            </FieldLabel>
            <div className="max-h-72 space-y-3 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
              {shipments.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-600">
                  {t(
                    'animalManagement.addToShipment.emptyState',
                    'No shipments match your search.',
                  )}
                </div>
              ) : (
                shipments.map((shipment) => {
                  const isSelected = shipment.id === selectedShipmentId;

                  return (
                    <Button
                      key={shipment.id}
                      type="button"
                      variant="ghost"
                      onClick={() => setSelectedShipmentId(shipment.id)}
                      className={cn(
                        'h-auto w-full flex-col items-stretch gap-3 rounded-lg border bg-white px-4 py-3 text-left transition-colors',
                        isSelected
                          ? 'border-blue-600 ring-2 ring-blue-100'
                          : 'border-slate-200 hover:border-slate-300',
                      )}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="font-medium text-slate-950">
                            {shipment.shipmentNumber}
                          </div>
                          <div className="text-sm text-slate-600">
                            {shipment.customer}
                          </div>
                        </div>
                        <ShipmentStatusTag status={shipment.status} />
                      </div>
                      <div className="text-sm text-slate-600">
                        {t('animalManagement.addToShipment.route', {
                          defaultValue: '{origin} to {destination}',
                          origin: shipment.origin,
                          destination: shipment.destination,
                        })}
                      </div>
                    </Button>
                  );
                })
              )}
            </div>
            {form.formState.errors.shipmentId && (
              <FieldError
                errors={[
                  {
                    ...form.formState.errors.shipmentId,
                    message: t('common.required', 'Required'),
                  },
                ]}
              />
            )}
          </Field>

          {conflicts.length > 0 && <ConflictSummary conflicts={conflicts} />}

          {conflicts.length > 0 && (
            <Controller
              name="overrideConflicts"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  orientation="horizontal"
                  data-invalid={fieldState.invalid}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950"
                >
                  <Checkbox
                    id={overrideCheckboxId}
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(checked === true)
                    }
                    className="mt-0.5"
                    aria-invalid={fieldState.invalid || undefined}
                  />
                  <FieldContent>
                    <FieldLabel
                      htmlFor={overrideCheckboxId}
                      className="text-sm font-medium text-amber-950"
                    >
                      {t(
                        'animalManagement.addToShipment.overrideLabel',
                        'I understand these animals will be moved from their current In Progress shipment into the selected shipment.',
                      )}
                    </FieldLabel>
                    <FieldDescription className="text-amber-900">
                      {t(
                        'animalManagement.addToShipment.conflictDescription',
                        'These animals are already assigned to an In Progress shipment. Confirm the move before continuing.',
                      )}
                    </FieldDescription>
                  </FieldContent>
                  {fieldState.error && (
                    <FieldError
                      errors={[
                        {
                          ...fieldState.error,
                          message: t(
                            'animalManagement.addToShipment.overrideRequired',
                            'You must confirm the shipment move before continuing.',
                          ),
                        },
                      ]}
                    />
                  )}
                </Field>
              )}
            />
          )}

          {requiresStatusChange && selectedShipment && (
            <Controller
              name="nextStatus"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Card className="border-violet-200 bg-violet-50/70">
                    <CardHeader>
                      <CardTitle className="text-sm font-semibold text-violet-950">
                        {t(
                          'animalManagement.addToShipment.statusChangeTitle',
                          'Shipment status update required',
                        )}
                      </CardTitle>
                      <CardDescription className="text-violet-900">
                        {t(
                          'animalManagement.addToShipment.statusChangeDescription',
                          {
                            defaultValue:
                              '{shipmentNumber} is currently {status}. Select a new status before adding animals to it.',
                            shipmentNumber: selectedShipment.shipmentNumber,
                            status: getShipmentStatusLabel(
                              selectedShipment.status,
                            ),
                          },
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Select
                        items={shipmentStatusItems}
                        value={field.value?.toString() ?? ''}
                        onChange={(value) => field.onChange(value)}
                      >
                        <SelectTrigger
                          aria-invalid={fieldState.invalid || undefined}
                        >
                          <SelectValue
                            placeholder={t(
                              'animalManagement.addToShipment.statusChangePlaceholder',
                              'Choose a shipment status',
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {shipmentStatusChangeOptions.map((option) => (
                            <SelectItem
                              key={option.value}
                              value={option.value.toString()}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {fieldState.error && (
                        <FieldError
                          errors={[
                            {
                              ...fieldState.error,
                              message: t(
                                'animalManagement.addToShipment.statusChangeRequired',
                                'Select a new shipment status to continue.',
                              ),
                            },
                          ]}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Field>
              )}
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <XIcon className="mr-2 size-4" />
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={selectedAnimals.length === 0}
          >
            <PackagePlusIcon className="mr-2 size-4" />
            {t('animalManagement.addToShipment.submit', 'Add to Shipment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
